import { gql, useLazyQuery } from "@apollo/client";
import { Loader2Icon } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";

import Checkbox from "@/components/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useUpdateUser from "@/graphql/mutations/use-update-user";
import useAuth from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { user } from "@/store";

import useGetOrCreateUser from "../hooks/use-get-or-create-user";
import { ErrorIcon } from "./icons";

export const INVITE_CODE_STORAGE_KEY = "invite_code";

interface InputWithLabelProps extends React.ComponentProps<typeof Input> {
  errors?: string[];
  label: string;
}

function InputWithLabel({
  className,
  errors = [],
  label,
  ...props
}: InputWithLabelProps) {
  const id = useId();

  if (errors.length) {
    props["aria-invalid"] = true;
  }

  return (
    <>
      <Label className="text-13/[1.5em] text-white" htmlFor={id}>
        {label}
      </Label>
      <Input
        aria-autocomplete="none"
        autoComplete="off"
        autoCorrect="off"
        className={cn(
          "h-12.5 rounded-[10px] border-white/20 bg-eerie-black text-16 text-white shadow-bright-red/64 transition-[colors,shadow] placeholder:text-white/40 focus-visible:border-azure aria-invalid:border-bright-red aria-invalid:shadow-0-0-10 md:text-14",
          className
        )}
        id={id}
        spellCheck="false"
        {...props}
      />
      {!!errors.length && (
        <ul className="flex flex-col gap-4">
          {errors.map((value) => (
            <li
              className="flex items-center gap-2 text-13/[1.9em] text-bright-red"
              key={value}
            >
              <ErrorIcon />
              {value}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

interface UsernameProps {
  onComplete(): void;
}

export default function Username({ onComplete }: UsernameProps) {
  const userData = user();

  const [form, setForm] = useState({
    email: userData?.email || "",
    newsletter: userData?.newsletter,
    username: userData?.isPublic ? userData.username || "" : "",
  });
  const [inviteCode, setInviteCode] = useState("");
  const [inviteCodeError, setInviteCodeError] = useState(false);
  const [inviteCodeInput, setInviteCodeInput] = useState(false);
  const [isInProgress, setIsInProgress] = useState(false);
  const [terms, setTerms] = useState(false);

  const { authToken, mutationContext } = useAuth();
  const [getUser] = useGetOrCreateUser();
  const [verifyInviteCode] = useLazyQuery<
    { inviteCode: { isActive: boolean } | null },
    { code: string }
  >(gql`
    query InviteCode($code: String!) {
      inviteCode(code: $code) {
        isActive
      }
    }
  `);
  const [updateUser, { data, loading }] = useUpdateUser();

  const handleInput = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    setForm({ ...form, [name]: value });
  };

  const handleInvideCodeButton = () => setInviteCodeInput(true);

  const handleInvideCodeInput = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setInviteCode(event.currentTarget.value);
  };

  const handleCheckbox = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const { checked, name } = event.currentTarget;

    if (name === "terms") {
      setTerms(checked);
    } else {
      setForm({ ...form, [name]: checked });
    }
  };

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsInProgress(true);

    if (inviteCode) {
      (async () => {
        const { data } = await verifyInviteCode({
          variables: { code: inviteCode },
        });

        if (data?.inviteCode?.isActive) {
          getUser({
            variables: {
              idToken: authToken,
              inviteCode,
            },
          });
          updateUser(mutationContext({ input: form }));
        } else {
          setInviteCodeError(true);
          setIsInProgress(false);
        }
      })();
    } else {
      updateUser(mutationContext({ input: form }));
    }
  };

  useEffect(() => {
    if (data) {
      setIsInProgress(false);

      if (!(data.updateUser.errors || []).length) {
        user(data.updateUser.user);
        onComplete();
      }
    }
  }, [data, onComplete]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: getUser wont change
  useEffect(() => {
    if (!authToken) {
      return;
    }

    const code = sessionStorage.getItem(INVITE_CODE_STORAGE_KEY);

    if (!code) {
      setInviteCodeInput(true);
      return;
    }

    (async () => {
      const { data } = await verifyInviteCode({ variables: { code } });

      if (data?.inviteCode?.isActive) {
        getUser({
          variables: {
            idToken: authToken,
            inviteCode: code,
          },
        });
      } else {
        setInviteCodeInput(true);
      }
    })();
  }, [authToken]);

  const errorsByField = useMemo(() => {
    if (!data?.updateUser.errors) {
      return {};
    }

    return data.updateUser.errors.reduce(
      (accum, error) => {
        accum[error.field] = error.messages;
        return accum;
      },
      {} as Record<string, string[]>
    );
  }, [data]);

  const emailRequired = !userData?.email;

  const isValid = (() => {
    if (loading) {
      return false;
    }
    if (!form.username) {
      return false;
    }
    if (!terms) {
      return false;
    }
    if (emailRequired && !form.email) {
      return false;
    }
    return true;
  })();

  return (
    <form
      autoComplete="off"
      autoCorrect="off"
      onSubmit={handleSubmit}
      spellCheck="false"
    >
      <div className="flex flex-col items-stretch gap-4 py-2.5">
        {emailRequired && (
          <InputWithLabel
            errors={errorsByField.email || []}
            label="Enter your email"
            name="email"
            onChange={handleInput}
            placeholder="Email *"
            value={form.email}
          />
        )}
        <InputWithLabel
          errors={errorsByField.username || []}
          label="Enter a username so the community can connect with you."
          name="username"
          onChange={handleInput}
          placeholder="Username *"
          value={form.username}
        />
        {inviteCodeInput ? (
          <InputWithLabel
            errors={inviteCodeError ? ["Invalid invite code!"] : []}
            label="Do you have an invite code? (optional)"
            onChange={handleInvideCodeInput}
            placeholder="Invite code"
            value={inviteCode}
          />
        ) : (
          <Button
            className="h-5 justify-start font-medium text-13 text-white underline hover:no-underline"
            onClick={handleInvideCodeButton}
          >
            Do you have an invite code? (optional)
          </Button>
        )}
        <Checkbox
          checked={form.newsletter}
          labelClassName="text-white text-13/[1.5em]"
          name="newsletter"
          onChange={handleCheckbox}
        >
          I’d like to get the email newsletter
        </Checkbox>
        <Checkbox
          checked={terms}
          labelClassName="text-white text-13/[1.5em]"
          name="terms"
          onChange={handleCheckbox}
        >
          I agree to the{" "}
          <a
            className="font-medium underline hover:text-bright-pink"
            href="https://link.morty.app/terms-of-use"
            rel="noopener noreferrer"
            target="_blank"
          >
            Terms of use
          </a>{" "}
          and{" "}
          <a
            className="font-medium underline hover:text-bright-pink"
            href="https://link.morty.app/privacy-notice"
            rel="noopener noreferrer"
            target="_blank"
          >
            Privacy policy
          </a>{" "}
          <span className="text-sky-blue leading-[1em]">*</span>
        </Checkbox>
      </div>
      <Button
        className="mt-4 h-12.5 w-full rounded-[25px] transition-opacity aria-invalid:opacity-30"
        disabled={!isValid || isInProgress}
        type="submit"
        variant="cta"
        {...(!isValid && { "aria-invalid": true })}
      >
        {isInProgress ? <Loader2Icon className="animate-spin" /> : "Continue"}
      </Button>
    </form>
  );
}
